import React, { useMemo } from 'react'
import { FormControlLabel, Hidden, makeStyles, Switch, Typography } from '@paintscout/ui'
import { EstimatorAvatar, useUsers } from '@paintscout/ui-business'
import { removeAuth0Prefix } from '@paintscout/util'
import type { CalendarEvent } from 'paintscout'
import type { View } from 'react-big-calendar'
import { useCalendar } from './CalendarContext'
import { getViewRange } from './calendarUtils'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    backgroundColor: theme.palette.common.white
  },
  totalsScroller: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'visible',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    flex: 1,
    '&::-webkit-scrollbar': {
      height: '4px'
    }
  },
  totalGroup: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    fontSize: theme.typography.pxToRem(13),
    whiteSpace: 'nowrap'
  },
  totalLabel: {
    color: theme.paintscout.palette.brand.Neutral.P50,
    fontWeight: 400
  },
  totalAmount: {
    color: theme.paintscout.palette.brand.Neutral.P30,
    fontWeight: 600
  },
  divider: {
    width: '1px',
    height: '20px',
    backgroundColor: theme.paintscout.palette.brand.Neutral.P85,
    flexShrink: 0
  },
  perUser: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexShrink: 0
  },
  perUserAmount: {
    color: theme.paintscout.palette.brand.Neutral.P30,
    fontSize: theme.typography.pxToRem(13),
    whiteSpace: 'nowrap'
  },
  weekendsSwitch: {
    color: theme.paintscout.palette.brand.Neutral.P50,
    flexShrink: 0,
    margin: 0,
    '& label': {
      marginLeft: theme.spacing(1)
    }
  }
}))

interface SalesCalendarFooterProps {
  view: View
  date: Date
  events: CalendarEvent[]
}

export default function SalesCalendarFooter(props: SalesCalendarFooterProps) {
  const { view, date, events } = props
  const classes = useStyles()
  const { hideWeekends, setHideWeekends } = useCalendar()
  const { users, userPictures } = useUsers()

  const { total, perUser } = useMemo(() => {
    const { start, end } = getViewRange(date, view)
    const rangeEvents = events.filter((event) => {
      if (!event.start || !event.end) return false
      const eventStart = new Date(event.start)
      const eventEnd = new Date(event.end)
      return eventStart <= end && eventEnd >= start
    })

    const perUserMap = new Map<string, number>()
    rangeEvents.forEach((event) => {
      const estimatorId = event.estimator?.id
      if (!estimatorId) return
      perUserMap.set(estimatorId, (perUserMap.get(estimatorId) || 0) + 1)
    })

    const perUser = Array.from(perUserMap.entries())
      .map(([id, count]) => ({ id, count }))
      .sort((a, b) => b.count - a.count)

    const total = perUser.reduce((sum, u) => sum + u.count, 0)

    return { total, perUser }
  }, [events, date, view])

  const totalLabel = view === 'month' ? 'Total month' : view === 'week' ? 'Total week' : 'Total'

  return (
    <div className={classes.root}>
      <div className={classes.totalsScroller}>
        <div className={classes.totalGroup}>
          <span className={classes.totalLabel}>{totalLabel}:</span>
          <span className={classes.totalAmount}>{total}</span>
        </div>
        {perUser.length > 0 && <div className={classes.divider} />}
        <Hidden xsDown>
          {perUser.map(({ id, count }) => {
            const user = users?.find((u) => removeAuth0Prefix(u.user_id) === removeAuth0Prefix(id))
            if (!user) return null
            return (
              <div key={id} className={classes.perUser}>
                <EstimatorAvatar
                  user={{
                    id: user.user_id,
                    firstName: user.user_metadata?.firstName,
                    lastName: user.user_metadata?.lastName
                  }}
                  userPictures={userPictures}
                  borderColor={user.user_metadata?.color}
                  width="22px"
                  height="22px"
                />
                <Typography className={classes.perUserAmount}>{count}</Typography>
              </div>
            )
          })}
        </Hidden>
      </div>
      <FormControlLabel
        className={classes.weekendsSwitch}
        label="Weekends"
        labelPlacement="start"
        control={<Switch checked={!hideWeekends} onChange={() => setHideWeekends(!hideWeekends)} />}
      />
    </div>
  )
}
